/**
 * The choices that follow a dispatcher from desk to desk.
 *
 * Which view opens, the language, the theme, how much of the desk is map and whether cars
 * on a break are drawn used to live in the browser of whichever PC the dispatcher was at,
 * on the theory that a workstation is a place in the office. The office disagreed: two
 * dispatchers share a desk across shifts, and each wants their own console back. So they
 * are kept on the membership — `GET/PUT /api/memberships/me/console` — read once at sign-in
 * and written on every change.
 *
 * The browser copy stays, as a cache: the sign-in page needs a theme and a language before
 * anybody has signed in, and `index.html` stamps the theme before the first paint from the
 * same key. Sign-in overwrites the cache with the person's own choices, so the next load at
 * this PC paints right before the fetch comes back, and the next person's sign-in
 * overwrites it again with theirs.
 *
 * What stays with the PC, deliberately: the desk phone and the microphone and speaker.
 * They are plugged into it.
 */

import { request, type Narrow, type Schema } from '@taxi/api-client';

import type { View } from './views';
import type { Preference as ThemePreference } from '../theme';

export type ConsolePreferences = Narrow<
  Schema<'ConsolePreferences'>,
  {
    defaultView: View | null;
    theme: ThemePreference | null;
    entrances?: 'list' | 'after' | null;
    numbers?: 'list' | 'after' | null;
  }
>;

export const NOTHING_CHOSEN: ConsolePreferences = {
  defaultView: null,
  language: null,
  theme: null,
  deskWidth: null,
  showOnBreak: null,
  entrances: null,
  numbers: null,
};

/**
 * Who this person is at the firm, and which areas of the console are theirs.
 *
 * Asked of the server rather than read off a role: the answer is the policy that guards the
 * settings and the reports, evaluated for this person — and What's new, which follows it. A dispatcher is refused both, so the
 * bar draws neither — a button that only ever opens onto a refusal is not a button.
 */
export type ConsoleAccess = Schema<'ConsoleAccess'>;

/** What a console that could not ask assumes: nothing beyond the desk. */
export const DESK_ONLY: ConsoleAccess = {
  displayName: null,
  email: null,
  settings: false,
  reports: false,
  whatsNew: false,
  calls: false,
};

export const preferencesApi = {
  mine: () => request<ConsolePreferences>('/api/memberships/me/console'),

  access: () => request<ConsoleAccess>('/api/memberships/me/access'),

  /** The whole document every time: a field left out is a choice withdrawn. */
  save: (preferences: ConsolePreferences) =>
    request<void>('/api/memberships/me/console', { method: 'PUT', body: preferences }),
};
